import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '@/src/store'
import {
    getBookingDetails,
    uploadDocuments
} from '@/src/api/services/visaService'
import { BookingDetails } from '@/src/types/visa/bookingDetails'

interface DocumentsState {
    bookingDetails: {
        data: BookingDetails | null
        isLoading: boolean
        error: any
    }
    uploadStatus: {
        isUploading: boolean
        success: boolean
        error: any
        uploadedDocuments: Record<string, boolean> // Track uploaded documents by key
    }
    // New field to store documents between page loads
    savedDocuments: Record<string, File>
}

const initialState: DocumentsState = {
    bookingDetails: {
        data: null,
        isLoading: false,
        error: null
    },
    uploadStatus: {
        isUploading: false,
        success: false,
        error: null,
        uploadedDocuments: {}
    },
    // Initialize saved documents as empty
    savedDocuments: {}
}

// Async thunk for fetching booking details
export const fetchBookingDetails = createAsyncThunk(
    'documents/fetchBookingDetails',
    async (itineraryId: string) => {
        const response = await getBookingDetails(itineraryId)
        return response
    }
)

// New async thunk for uploading documents
export const uploadDocumentsBatch = createAsyncThunk(
    'documents/uploadDocumentsBatch',
    async ({
        itineraryId,
        files
    }: {
        itineraryId: string
        files: Record<string, File>
    }) => {
        // Create form data with all files
        const formData = new FormData()
        Object.entries(files).forEach(([key, file]) => {
            formData.append(key, file)
        })

        const response = await uploadDocuments(itineraryId, formData)
        return response
    }
)

const documentsSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
        resetUploadStatus: (state) => {
            state.uploadStatus = {
                isUploading: false,
                success: false,
                error: null,
                uploadedDocuments: {}
            }
        },
        markDocumentAsUploaded: (state, action) => {
            // Mark a specific document as uploaded
            state.uploadStatus.uploadedDocuments[action.payload] = true
        },
        markAllDocumentsUploaded: (state, action) => {
            // Mark all documents as uploaded from an array of keys
            const documentKeys = action.payload
            documentKeys.forEach((key: string) => {
                state.uploadStatus.uploadedDocuments[key] = true
            })
        },
        setUploadSuccess: (state, action) => {
            state.uploadStatus.success = action.payload
        },
        setUploadError: (state, action) => {
            state.uploadStatus.error = action.payload
        },
        // New reducer to save documents to state
        saveDocuments: (state, action) => {
            state.savedDocuments = {
                ...state.savedDocuments,
                ...action.payload
            }
        },
        clearSavedDocuments: (state) => {
            state.savedDocuments = {}
        }
    },
    extraReducers: (builder) => {
        // Handle fetchBookingDetails
        builder
            .addCase(fetchBookingDetails.pending, (state) => {
                state.bookingDetails.isLoading = true
                state.bookingDetails.error = null
            })
            .addCase(fetchBookingDetails.fulfilled, (state, action) => {
                state.bookingDetails.isLoading = false
                const responseData = action.payload.response || action.payload
                state.bookingDetails.data = responseData as BookingDetails
            })
            .addCase(fetchBookingDetails.rejected, (state, action) => {
                state.bookingDetails.isLoading = false
                state.bookingDetails.error = action.error
                state.bookingDetails.data = {} as BookingDetails
            })

        // Handle uploadDocumentsBatch
        builder
            .addCase(uploadDocumentsBatch.pending, (state) => {
                state.uploadStatus.isUploading = true
                state.uploadStatus.error = null
            })
            .addCase(uploadDocumentsBatch.fulfilled, (state, action) => {
                state.uploadStatus.isUploading = false
                state.uploadStatus.success = true

                // Mark all documents as uploaded
                Object.keys(state.savedDocuments).forEach((key) => {
                    state.uploadStatus.uploadedDocuments[key] = true
                })
            })
            .addCase(uploadDocumentsBatch.rejected, (state, action) => {
                state.uploadStatus.isUploading = false
                state.uploadStatus.error = action.error
            })
    }
})

export const {
    resetUploadStatus,
    markDocumentAsUploaded,
    markAllDocumentsUploaded,
    setUploadSuccess,
    setUploadError,
    saveDocuments,
    clearSavedDocuments
} = documentsSlice.actions

// Selectors
export const selectBookingDetails = (state: RootState) =>
    state.documents.bookingDetails
export const selectUploadStatus = (state: RootState) =>
    state.documents.uploadStatus
export const selectSavedDocuments = (state: RootState) =>
    state.documents.savedDocuments

export default documentsSlice.reducer
